import {
  NJChatInputActionButton,
  NJChatInputFileCard,
  NJIcon,
  NJIconButton,
  NJText,
} from "@engie-group/fluid-design-system-react";
import {
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { ContextUsage, UploadedFile } from "@/types";
import { useChat } from "./ChatProvider";
import { useUploadFile } from "./useUploadFile";

const GAUGE_SIZE = 26;
const GAUGE_STROKE = 2.5;
const GAUGE_RADIUS = (GAUGE_SIZE - GAUGE_STROKE) / 2;
const GAUGE_CIRCUMFERENCE = 2 * Math.PI * GAUGE_RADIUS;

/** Circular context-usage gauge with a hover/focus tooltip (Used / Limit / Usage / Remaining) —
 * matches the legacy composer bubble. */
function ContextGauge({ usage }: { usage: ContextUsage }) {
  const tooltipId = useId();
  const percent = Math.min(100, Math.max(0, usage.contextPercent));
  const dashOffset = GAUGE_CIRCUMFERENCE * (1 - percent / 100);
  const window = Math.max(1, usage.contextWindow);
  const used = Math.max(0, usage.contextUsed);
  const remaining = Math.max(0, window - used);
  const over = Math.max(0, used - window);

  return (
    <div
      className="group relative inline-flex h-[26px] w-[26px] flex-shrink-0 items-center justify-center outline-none"
      role="img"
      tabIndex={0}
      aria-describedby={tooltipId}
      aria-label={`Context used: ${used} of ${window} tokens (${percent}%).`}
    >
      <svg
        width={GAUGE_SIZE}
        height={GAUGE_SIZE}
        viewBox={`0 0 ${GAUGE_SIZE} ${GAUGE_SIZE}`}
      >
        <circle
          style={{
            stroke:
              "var(--nj-semantic-color-border-neutral-subtle-default, #d0d5dd)",
          }}
          cx={GAUGE_SIZE / 2}
          cy={GAUGE_SIZE / 2}
          r={GAUGE_RADIUS}
          strokeWidth={GAUGE_STROKE}
          fill="none"
        />
        <circle
          className="transition-[stroke-dashoffset] duration-300"
          style={{
            stroke:
              "var(--nj-semantic-color-icon-brand-primary-default, #007bc5)",
          }}
          cx={GAUGE_SIZE / 2}
          cy={GAUGE_SIZE / 2}
          r={GAUGE_RADIUS}
          strokeWidth={GAUGE_STROKE}
          fill="none"
          strokeDasharray={GAUGE_CIRCUMFERENCE}
          strokeDashoffset={dashOffset}
          strokeLinecap="round"
          transform={`rotate(-90 ${GAUGE_SIZE / 2} ${GAUGE_SIZE / 2})`}
        />
      </svg>
      <div
        id={tooltipId}
        role="tooltip"
        className="pointer-events-none invisible absolute bottom-[calc(100%+8px)] right-0 z-30 grid min-w-[212px] gap-1 rounded-[10px] border border-nj-neutral-subtle-default bg-nj-neutral-primary-default p-2 opacity-0 shadow-lg transition group-hover:visible group-hover:opacity-100 group-focus:visible group-focus:opacity-100"
      >
        <GaugeRow label="Used" value={`${used.toLocaleString()} tokens`} />
        <GaugeRow label="Limit" value={`${window.toLocaleString()} tokens`} />
        <GaugeRow label="Usage" value={`${percent}%`} />
        <GaugeRow
          label={over > 0 ? "Over" : "Remaining"}
          value={`${(over > 0 ? over : remaining).toLocaleString()} tokens`}
          danger={over > 0}
        />
      </div>
    </div>
  );
}

function GaugeRow({
  label,
  value,
  danger,
}: {
  label: string;
  value: string;
  danger?: boolean;
}) {
  return (
    <div className="flex items-center justify-between gap-2.5 text-xs text-nj-neutral-primary-default">
      <span className="text-nj-neutral-secondary-default">{label}</span>
      <strong
        style={
          danger
            ? {
                color:
                  "var(--nj-semantic-color-text-status-danger-solid-default, #c9252d)",
              }
            : undefined
        }
      >
        {value}
      </strong>
    </div>
  );
}

/** Persistent chat composer docked at the bottom of every page (non-admin), reproducing the
 * legacy design: big auto-growing textarea, attachment button on the left, a borderless custom
 * agent selector (locked once a session is active) and the circular context gauge. */
export function ChatComposer() {
  const {
    activeSession,
    grantedAgents,
    selectedAgentId,
    setSelectedAgentId,
    isSending,
    isStreaming,
    send,
  } = useChat();

  const [draft, setDraft] = useState("");
  const [attachments, setAttachments] = useState<UploadedFile[]>([]);
  const [menuOpen, setMenuOpen] = useState(false);
  const uploadFile = useUploadFile();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const agentBlockRef = useRef<HTMLDivElement>(null);

  const isClosed = Boolean(activeSession) && activeSession!.status !== "ACTIVE";
  const locked = Boolean(activeSession);
  const currentAgentName = useMemo(() => {
    const slug = activeSession ? activeSession.agentId : selectedAgentId;
    return grantedAgents.find((agent) => agent.slug === slug)?.name ?? "Auto";
  }, [activeSession, selectedAgentId, grantedAgents]);

  const canSend =
    !isSending &&
    !isClosed &&
    draft.trim().length > 0 &&
    (locked || Boolean(selectedAgentId));

  // Auto-grow the textarea up to ~32dvh (legacy behaviour).
  useLayoutEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    textarea.style.height = "";
    const next = Math.min(
      Math.max(textarea.scrollHeight, 52),
      window.innerHeight * 0.32,
    );
    textarea.style.height = `${next}px`;
  }, [draft]);

  // Close the agent menu on outside click / scroll / route change.
  useEffect(() => {
    if (!menuOpen) return;
    const close = () => setMenuOpen(false);
    const onClick = (event: MouseEvent) => {
      if (
        agentBlockRef.current &&
        event.target instanceof Node &&
        !agentBlockRef.current.contains(event.target)
      ) {
        close();
      }
    };
    window.addEventListener("click", onClick);
    window.addEventListener("scroll", close, true);
    return () => {
      window.removeEventListener("click", onClick);
      window.removeEventListener("scroll", close, true);
    };
  }, [menuOpen]);

  useEffect(() => setMenuOpen(false), [activeSession?.id]);

  const submit = () => {
    if (!canSend) return;
    const content = draft.trim();
    const current = attachments;
    setDraft("");
    setAttachments([]);
    send(content, current);
  };

  const onFilesSelected = (files: FileList | null) => {
    if (!files) return;
    for (const file of Array.from(files)) {
      uploadFile.mutate(file, {
        onSuccess: (uploaded) => setAttachments((prev) => [...prev, uploaded]),
      });
    }
  };

  return (
    <footer className="grid gap-1.5 border-t border-nj-neutral-subtle-default bg-nj-neutral-primary-default px-4 pb-6 pt-3 sm:px-6">
      <div className="flex w-full flex-col border border-nj-neutral-moderate-default bg-nj-neutral-primary-default shadow-sm">
        {attachments.length > 0 && (
          <div className="flex items-center gap-2 overflow-x-auto px-3.5 pt-1.5">
            {attachments.map((file) => (
              <NJChatInputFileCard
                key={file.id}
                fileName={file.fileName}
                fileType={file.mimeType ?? undefined}
                onDelete={() =>
                  setAttachments((prev) =>
                    prev.filter((item) => item.id !== file.id),
                  )
                }
              />
            ))}
          </div>
        )}

        <textarea
          ref={textareaRef}
          name="input"
          rows={1}
          aria-label="Message input"
          placeholder={
            isClosed ? "Closed session — read only" : "Ask me something…"
          }
          disabled={isClosed || isStreaming}
          value={draft}
          onChange={(event) => setDraft(event.currentTarget.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter" && !event.shiftKey) {
              event.preventDefault();
              submit();
            }
          }}
          className="max-h-[32dvh] min-h-[52px] w-full resize-none border-0 bg-transparent px-5 pb-3 pt-4 text-nj-neutral-primary-default outline-none [overscroll-behavior:contain]"
        />

        <div className="flex items-center justify-between gap-2 px-2.5 pb-2.5">
          <NJIconButton
            label="Add attachment"
            title="Add attachment"
            icon="attach_file"
            scale="md"
            variant="tertiary"
            isDisabled={isClosed || isStreaming}
            onClick={() => fileInputRef.current?.click()}
          />

          <div className="flex min-w-0 items-center gap-1">
            {activeSession && (
              <ContextGauge usage={activeSession.contextUsage} />
            )}

            <div className="relative" ref={agentBlockRef}>
              <button
                type="button"
                disabled={locked || grantedAgents.length === 0}
                aria-haspopup="menu"
                aria-expanded={menuOpen}
                title={
                  locked ? "Locked for this conversation" : "Available agents"
                }
                onClick={() => setMenuOpen((prev) => !prev)}
                className="inline-flex max-w-[10rem] items-center gap-1 rounded-md bg-transparent px-2 py-1 text-sm text-nj-brand-solid-default transition hover:bg-nj-brand-secondary-default disabled:cursor-default disabled:hover:bg-transparent"
              >
                <span className="max-w-[8rem] truncate">
                  {currentAgentName}
                </span>
                <NJIcon name={locked ? "lock" : "expand_more"} />
              </button>

              {!locked && menuOpen && grantedAgents.length > 0 && (
                <div
                  role="menu"
                  aria-label="Available agents"
                  className="absolute bottom-[calc(100%+8px)] right-0 z-20 grid max-w-[min(380px,80vw)] gap-0.5 rounded-2xl border border-nj-neutral-subtle-default bg-nj-neutral-primary-default p-2 shadow-lg min-w-[280px]"
                >
                  {grantedAgents.map((agent) => (
                    <button
                      key={agent.slug}
                      type="button"
                      role="menuitem"
                      onClick={() => {
                        setSelectedAgentId(agent.slug);
                        setMenuOpen(false);
                      }}
                      className={`grid grid-cols-[1fr_auto] items-center gap-2 rounded-md px-2.5 py-2 text-left transition hover:bg-nj-neutral-secondary-default ${
                        agent.slug === selectedAgentId
                          ? "bg-nj-brand-secondary-default"
                          : ""
                      }`}
                    >
                      <span className="grid min-w-0 gap-px">
                        <strong className="truncate">{agent.name}</strong>
                        <span className="truncate text-xs text-nj-neutral-secondary-default">
                          {agent.description}
                        </span>
                      </span>
                      {agent.slug === selectedAgentId && (
                        <NJIcon name="check" />
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <NJChatInputActionButton
              variant="ai"
              aria-label="Send"
              disabled={!canSend}
              onClick={submit}
            />
          </div>
        </div>
      </div>

      {!locked && grantedAgents.length === 0 && (
        <NJText as="p" scale="sm" variant="warning">
          You don&apos;t have access to any agent yet — request access from the
          Agent Lib.
        </NJText>
      )}

      <input
        ref={fileInputRef}
        type="file"
        multiple
        className="hidden"
        accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,image/*"
        onChange={(event) => {
          onFilesSelected(event.target.files);
          event.target.value = "";
        }}
      />
    </footer>
  );
}
